import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { dollars, intFmt } from "@/lib/money";

const SERVICE_COLORS = [
  "hsl(var(--brand))",
  "hsl(38 96% 56%)",
  "hsl(142 64% 44%)",
  "hsl(199 89% 56%)",
  "hsl(330 80% 60%)",
  "hsl(280 84% 64%)",
];

export function Costs() {
  const summaryQ = useQuery({ queryKey: ["costs", "summary"], queryFn: api.costsSummary });
  const timeseriesQ = useQuery({
    queryKey: ["costs", "timeseries"],
    queryFn: () => api.costsTimeseries({ days: 14, group_by: "day" }),
  });
  const byCreatorQ = useQuery({
    queryKey: ["costs", "by_creator"],
    queryFn: () => api.costsByCreator({ days: 7 }),
  });
  const byServiceQ = useQuery({
    queryKey: ["costs", "by_service"],
    queryFn: () => api.costsByService({ days: 7 }),
  });
  const byJobTypeQ = useQuery({
    queryKey: ["costs", "by_job_type"],
    queryFn: () => api.costsByJobType({ days: 7 }),
  });

  const chartData = React.useMemo(() => {
    const pts = timeseriesQ.data ?? [];
    return pts.map((p) => ({
      day: p.bucket,
      ...p.by_service,
      total: p.cost_cents,
    }));
  }, [timeseriesQ.data]);

  const services = React.useMemo(() => {
    const keys = new Set<string>();
    for (const p of timeseriesQ.data ?? []) {
      if (p.by_service) for (const k of Object.keys(p.by_service)) keys.add(k);
    }
    return Array.from(keys);
  }, [timeseriesQ.data]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Costs</h1>
        <p className="text-xs text-muted-foreground">
          All-in spend. Hard cap is $1,000/mo; pages above $40/day.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-3">
        {summaryQ.isLoading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)
        ) : summaryQ.data ? (
          <>
            <CostStat
              label="Today"
              value={dollars(summaryQ.data.today_cents)}
              extra={
                <Progress
                  value={Math.min(100, summaryQ.data.pct_of_today)}
                  tone={
                    summaryQ.data.pct_of_today >= 100
                      ? "danger"
                      : summaryQ.data.pct_of_today >= 75
                        ? "warning"
                        : "brand"
                  }
                  className="mt-2"
                />
              }
              hint={`${summaryQ.data.pct_of_today.toFixed(0)}% of ${dollars(summaryQ.data.ceiling_today_cents)} ceiling`}
            />
            <CostStat label="Last 7 days" value={dollars(summaryQ.data.d7_cents)} />
            <CostStat label="Last 30 days" value={dollars(summaryQ.data.d30_cents)} />
          </>
        ) : (
          <div className="md:col-span-3">
            <Empty title="No cost data." />
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cost by service — last 14 days</CardTitle>
          <CardDescription>Stacked. Hover for breakdown.</CardDescription>
        </CardHeader>
        <CardContent>
          {timeseriesQ.isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : chartData.length === 0 ? (
            <Empty title="No timeseries yet." />
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <AreaChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" />
                  <XAxis
                    dataKey="day"
                    fontSize={10}
                    stroke="hsl(var(--muted-foreground))"
                    tickFormatter={(s) => new Date(s).toLocaleDateString().slice(0, 5)}
                  />
                  <YAxis
                    fontSize={10}
                    stroke="hsl(var(--muted-foreground))"
                    tickFormatter={(v) => "$" + (Number(v) / 100).toFixed(0)}
                  />
                  <RTooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      fontSize: 12,
                    }}
                    formatter={(v: number) => dollars(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(services.length ? services : ["total"]).map((svc, i) => (
                    <Area
                      key={svc}
                      type="monotone"
                      dataKey={svc}
                      stackId={services.length ? "1" : undefined}
                      stroke={SERVICE_COLORS[i % SERVICE_COLORS.length]}
                      fill={SERVICE_COLORS[i % SERVICE_COLORS.length]}
                      fillOpacity={0.3}
                      strokeWidth={1.5}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By creator (7d)</CardTitle>
          </CardHeader>
          <CardContent>
            <CostTable
              rows={byCreatorQ.data ?? []}
              isLoading={byCreatorQ.isLoading}
              keyName="handle"
              labelHeader="Creator"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>By service (7d)</CardTitle>
          </CardHeader>
          <CardContent>
            <CostTable
              rows={byServiceQ.data ?? []}
              isLoading={byServiceQ.isLoading}
              keyName="service"
              labelHeader="Service"
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By job type (7d)</CardTitle>
        </CardHeader>
        <CardContent>
          <CostTable
            rows={byJobTypeQ.data ?? []}
            isLoading={byJobTypeQ.isLoading}
            keyName="job_type"
            labelHeader="Job type"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function CostStat({
  label,
  value,
  hint,
  extra,
}: {
  label: string;
  value: string;
  hint?: string;
  extra?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="px-5 py-4">
        <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="tnum text-2xl font-semibold">{value}</div>
        {hint ? <div className="mt-0.5 text-2xs text-muted-foreground">{hint}</div> : null}
        {extra}
      </CardContent>
    </Card>
  );
}

function CostTable<T extends { cost_cents: number }>({
  rows,
  isLoading,
  keyName,
  labelHeader,
}: {
  rows: T[];
  isLoading: boolean;
  keyName: keyof T & string;
  labelHeader: string;
}) {
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!rows.length) return <Empty title="No costs in this period." />;

  const total = rows.reduce((acc, r) => acc + r.cost_cents, 0);
  const sorted = [...rows].sort((a, b) => b.cost_cents - a.cost_cents);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{labelHeader}</TableHead>
          <TableHead className="text-right">Cost</TableHead>
          <TableHead className="text-right">Share</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((r, i) => (
          <TableRow key={i}>
            <TableCell className="text-xs">
              {String((r as Record<string, unknown>)[keyName] ?? "—")}
            </TableCell>
            <TableCell className="tnum text-right">{dollars(r.cost_cents)}</TableCell>
            <TableCell className="tnum text-right text-muted-foreground">
              {total ? ((r.cost_cents / total) * 100).toFixed(1) : "0.0"}%
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell className="text-2xs uppercase tracking-wide text-muted-foreground">
            total
          </TableCell>
          <TableCell className="tnum text-right font-semibold">{dollars(total)}</TableCell>
          <TableCell className="tnum text-right text-muted-foreground">
            {intFmt(rows.length)} rows
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
